/**
 * @file Клиентский кэш ответов /projects/price.
 *
 * Ползунок ездит туда-обратно, и один и тот же состав пересчитывается по
 * многу раз. TTL совпадает с серверным: повторный набор параметров берётся из
 * локального Map, а параллельные вызовы с одним ключом ждут общий промис,
 * чтобы на каждое движение ползунка не уходил свой запрос.
 */

import client from 'client';

const TTL_MS = 5 * 60 * 1000;

/** key → { value, expiresAt } */
const responseCache = new Map();
/** key → Promise */
const inFlight = new Map();

function buildCacheKey(payload) {
  const activities = [...payload.activities]
    .map(a => `${a.type}:${a.count}:${a.warranty ? 1 : 0}`)
    .sort()
    .join('|');
  const range = payload.range
    ? `${payload.range.axis}:${payload.range.min}:${payload.range.max}`
    : '';
  // range обязан быть в ключе: от него зависят границы «от … до» в ответе.
  // Тот же набор ключей собирает сервер (pricing.service.js).
  return [
    payload.marketKey,
    payload.days ?? '',
    range,
    payload.useCommunalServer ? 1 : 0,
    payload.testing ? 1 : 0,
    activities,
  ].join('|');
}

/**
 * Ответ из кэша, если он там есть и не протух. Синхронно — чтобы вернуть
 * ползунок на прежнее деление без промежуточного состояния «считаем».
 * @param {object} payload
 * @returns {?object}
 */
export function readPriceFromCache(payload) {
  const cached = responseCache.get(buildCacheKey(payload));
  return cached && cached.expiresAt > Date.now() ? cached.value : null;
}

/**
 * Запросить цену у сервера с кэшем и дедупликацией параллельных запросов.
 * @param {object} payload
 * @returns {Promise<{price: number, breakdown: object, pricing: object, currency: string}>}
 */
export function fetchPriceWithCache(payload) {
  const key = buildCacheKey(payload);
  const now = Date.now();

  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > now)
    return Promise.resolve(cached.value);

  let promise = inFlight.get(key);
  if (!promise) {
    promise = client.calculatePrice(payload)
      .then(res => {
        responseCache.set(key, { value: res, expiresAt: Date.now() + TTL_MS });
        return res;
      })
      .finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
  }
  return promise;
}
